import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GiveawayFilters = ({ filters, onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categoryOptions = [
    { value: 'all', label: 'All Categories', icon: 'Grid3X3' },
    { value: 'tech', label: 'Technology', icon: 'Smartphone' },
    { value: 'lifestyle', label: 'Lifestyle', icon: 'Coffee' },
    { value: 'experiences', label: 'Experiences', icon: 'Camera' }
  ];

  const entryMethodOptions = [
    { value: 'all', label: 'All Methods', icon: 'Hash' },
    { value: 'code', label: 'Code Entry', icon: 'Hash' },
    { value: 'social', label: 'Social Sharing', icon: 'Share' },
    { value: 'purchase', label: 'Purchase Required', icon: 'ShoppingCart' }
  ];

  const prizeValueOptions = [
    { value: 'all', label: 'All Values', icon: 'DollarSign' },
    { value: 'under1000', label: 'Under $1,000', icon: 'DollarSign' },
    { value: '1000-2000', label: '$1,000 - $2,000', icon: 'DollarSign' },
    { value: 'over2000', label: 'Over $2,000', icon: 'DollarSign' }
  ];

  const sortOptions = [
    { value: 'ending', label: 'Ending Soon' },
    { value: 'newest', label: 'Newest' },
    { value: 'value', label: 'Prize Value' },
    { value: 'participants', label: 'Most Popular' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const activeFiltersCount = Object.values(filters).filter(value => 
    value !== 'all' && value !== 'active' && value !== 'ending' && value !== ''
  ).length;

  const clearAllFilters = () => {
    onFiltersChange({
      search: '',
      category: 'all',
      entryMethod: 'all',
      prizeValue: 'all',
      status: 'active',
      sortBy: 'ending'
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
      {/* Search and Quick Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400"
          />
          <Input
            type="text"
            placeholder="Search giveaways, sponsors, or prizes..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={isExpanded ? 'primary' : 'outline'}
            onClick={() => setIsExpanded(!isExpanded)}
            iconName="Filter"
            iconPosition="left"
            className="relative"
          >
            Filters
            {activeFiltersCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </Button>
          
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              onClick={clearAllFilters}
              iconName="X"
              size="sm"
              className="text-accent-600 hover:text-accent-800"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Quick Filter Pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['Featured', 'Ending Soon', 'High Value', 'Easy Entry'].map((pill) => (
          <button
            key={pill}
            className="px-3 py-1 bg-surface hover:bg-accent-100 rounded-full text-sm font-medium text-accent-700 transition-colors"
          >
            {pill}
          </button>
        ))}
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="border-t border-accent-100 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Category
              </label>
              <div className="space-y-2">
                {categoryOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange('category', option.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      filters.category === option.value
                        ? 'bg-primary text-white' :'bg-surface hover:bg-accent-50 text-accent-700'
                    }`}
                  >
                    <Icon name={option.icon} size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Entry Method Filter */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Entry Method
              </label>
              <div className="space-y-2">
                {entryMethodOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange('entryMethod', option.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      filters.entryMethod === option.value
                        ? 'bg-primary text-white' :'bg-surface hover:bg-accent-50 text-accent-700'
                    }`}
                  >
                    <Icon name={option.icon} size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Prize Value Filter */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Prize Value
              </label>
              <div className="space-y-2">
                {prizeValueOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange('prizeValue', option.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      filters.prizeValue === option.value
                        ? 'bg-primary text-white' :'bg-surface hover:bg-accent-50 text-accent-700'
                    }`}
                  >
                    <Icon name={option.icon} size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Options */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Sort By
              </label>
              <div className="space-y-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange('sortBy', option.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      filters.sortBy === option.value
                        ? 'bg-primary text-white' :'bg-surface hover:bg-accent-50 text-accent-700'
                    }`}
                  >
                    <Icon name="ArrowUpDown" size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiveawayFilters;